import React, { Dispatch, SetStateAction } from "react";

import { useHistory } from "react-router-dom";

import { EditEmployeeModal } from "manager/components/Modal";
import ROUTES from "manager/config/routes";
import {
  Employee,
  shouldShowResendInviteForEmployee,
} from "manager/interfaces/api/employee";
import { getManagerEmployeeStatusDisplay } from "manager/utils/employeeStatus";
import Icon from "shared/components/Icon";
import { UnderlineLink } from "shared/components/Links";
import Table from "shared/components/Table";
import Tag from "shared/components/Tag";
import Tooltip from "shared/components/Tooltip";
import useModal from "shared/hooks/useModal";
import { Pagination } from "shared/interfaces";

import {
  AssignEmployeeToPropertyParams,
  DeactivateEmployeeParams,
} from "../interface";

import {
  EmployeeNameCell,
  EmployeeNameStrong,
  EmployeeStatus,
  ManageEmployeesIconButton,
} from "./styled";

/** Tooltip & `aria-label` copy — keep in sync with acceptance criteria. */
export const CAN_INVITE_EMPLOYEES_TOOLTIP = "Can invite employees";

const INVITE_EMPLOYEES_TOOLTIP_TRIGGERS = ["hover", "focus", "click"] as const;

function employeeSortName(employee: Employee | undefined): string {
  if (!employee) return "";
  return `${employee.user?.firstName ?? ""} ${
    employee.user?.lastName ?? ""
  }`.trim();
}

interface EmployeesTableProps {
  employees: Employee[];
  loading: boolean;
  assignEmployeeToProperty: (params: AssignEmployeeToPropertyParams) => void;
  deactivateEmployee: (params: DeactivateEmployeeParams) => void;
  resendEmployeeInvite: (employee: Employee) => void;
  canResendEmployeeInvite: boolean;
  hasInputValue: boolean;
  pagination: Pagination;
  setPagination: Dispatch<SetStateAction<Pagination>>;
  employeesCount: number;
  showDeactivated?: boolean;
}

const EmployeesTable = ({
  employees,
  loading,
  assignEmployeeToProperty,
  deactivateEmployee,
  resendEmployeeInvite,
  canResendEmployeeInvite,
  hasInputValue,
  pagination,
  setPagination,
  employeesCount,
  showDeactivated = false,
}: EmployeesTableProps) => {
  const history = useHistory();
  const { openModalDialog } = useModal();
  const { page, pageSize } = pagination;

  const onPaginationChange = (currentPage: number) => {
    const newPagination = { page: currentPage, pageSize };
    setPagination(newPagination);
  };

  return (
    <Table
      alt
      searching={hasInputValue}
      loading={loading}
      columns={[
        {
          title: "Name",
          dataIndex: "name",
          key: "name",
          sorter: (a, b) =>
            employeeSortName(a.rawData).localeCompare(
              employeeSortName(b.rawData)
            ),
        },
        {
          title: "Email",
          dataIndex: "email",
          key: "email",
          sorter: (a, b) =>
            a.email.props.children.props.children.localeCompare(
              b.email.props.children.props.children
            ),
        },
        {
          title: "Assigned to Properties",
          dataIndex: "propertiesAssigned",
          key: "propertiesAssigned",
          sorter: (a, b) =>
            a.propertiesAssigned.props.children -
            b.propertiesAssigned.props.children,
        },
        {
          title: "Role",
          dataIndex: "role",
          key: "role",
          sorter: (a, b) =>
            a.role.props.children.localeCompare(b.role.props.children),
        },
        {
          title: "Status",
          dataIndex: "isActive",
          key: "isActive",
          sorter: (a, b) => {
            return a.isActive.props.children.localeCompare(
              b.isActive.props.children
            );
          },
        },
      ]}
      dropdownItems
      rows={
        !loading
          ? employees?.map((employee, index) => {
              const fullName = employeeSortName(employee);
              const statusDisplay = getManagerEmployeeStatusDisplay(employee);
              const showResendInvite =
                canResendEmployeeInvite &&
                shouldShowResendInviteForEmployee(employee);

              return {
                key: employee.id,
                rawData: employee,
                values: [
                  <EmployeeNameCell>
                    <EmployeeNameStrong data-testid={`employee-${index}-name`}>
                      {fullName}
                    </EmployeeNameStrong>
                    {employee.canInviteEmployee === true ? (
                      <Tooltip
                        title={CAN_INVITE_EMPLOYEES_TOOLTIP}
                        theme="dark"
                        trigger={INVITE_EMPLOYEES_TOOLTIP_TRIGGERS}
                      >
                        <ManageEmployeesIconButton
                          type="button"
                          aria-label={CAN_INVITE_EMPLOYEES_TOOLTIP}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Icon.ManageEmployeesShieldIcon aria-hidden />
                        </ManageEmployeesIconButton>
                      </Tooltip>
                    ) : null}
                    {showDeactivated && !employee.isActive && (
                      <Tag
                        size={Tag.SIZES.sm}
                        style={{
                          marginLeft: 8,
                          color: "#666",
                          background: "#f0f0f0",
                          border: "none",
                        }}
                      >
                        Deactivated
                      </Tag>
                    )}
                  </EmployeeNameCell>,
                  <span data-testid={`employee-${index}-email`}>
                    <UnderlineLink href={`mailto:${employee.user?.email}`}>
                      {employee.user?.email}
                    </UnderlineLink>
                  </span>,
                  <span data-testid={`employee-${index}-properties`}>
                    {employee.numAssignedProperties}
                  </span>,
                  <span data-testid={`employee-${index}-role`}>
                    {employee.role?.name}
                  </span>,
                  <EmployeeStatus
                    data-testid={`employee-${index}-status`}
                    variant={statusDisplay.variant}
                  >
                    {statusDisplay.label}
                  </EmployeeStatus>,
                ],
                dropdownItems: employee.isActive
                  ? [
                      {
                        key: "EDIT_EMPLOYEE",
                        label: "Edit employee",
                        onClick: () =>
                          openModalDialog(EditEmployeeModal, {
                            employeeData: {
                              employeeId: employee.id,
                              firstName: employee.user?.firstName,
                              lastName: employee.user?.lastName,
                              email: employee.user?.email,
                            },
                          }),
                      },
                      {
                        key: "ASSIGN_TO_PROPERTIES",
                        label: "Assign to properties",
                        onClick: () =>
                          assignEmployeeToProperty({
                            employeeId: employee.id,
                            firstName: employee.user?.firstName,
                            lastName: employee.user?.lastName,
                          }),
                      },
                      ...(showResendInvite
                        ? [
                            {
                              key: "RESEND_INVITE",
                              label: "Resend invite",
                              onClick: () => resendEmployeeInvite(employee),
                            },
                          ]
                        : []),
                      {
                        key: "DEACTIVATE",
                        label: "Deactivate",
                        onClick: () =>
                          deactivateEmployee({
                            employeeId: employee.id,
                            name: employee.user?.firstName,
                          }),
                      },
                    ]
                  : [
                      {
                        key: "DEACTIVATED",
                        label: "Employee was deactivated",
                        disabled: true,
                      },
                    ],
              };
            })
          : []
      }
      columnBreakpoints={{
        xs: {
          column1: "name",
          column2: "email",
        },
        sm: ["name", "email", "role"],
        md: ["name", "email", "role"],
      }}
      onRowClick={(key) => {
        history.push(ROUTES.employee.replace(":id", key));
      }}
      placeholder={{
        label: "Employees",
        Icon: Icon.NoEmployeesIcon,
      }}
      pagination={{
        current: page,
        onChange: onPaginationChange,
        total: employeesCount,
        defaultPageSize: pageSize,
      }}
    />
  );
};

export default EmployeesTable;
